import React from "react";
import { Calendar, Wand2, BellRing, ShieldCheck, Coins, LayoutDashboard, MessageSquareText } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import "./sidebar-styles.css";

export const MobileNav = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <nav className="fixed bottom-0 left-0 right-0 w-full bg-[#101525]/95 backdrop-blur-xl border-t border-indigo-700/40 md:hidden z-50 shadow-xl shadow-black/40">
      <div className="flex items-center justify-between px-1 py-1.5 max-w-screen-xl mx-auto overflow-x-auto gap-1 no-scrollbar">
        <NavButton 
          icon={<LayoutDashboard />} 
          firstLine="Strykr"
          secondLine="Dashboard" 
          isActive={location.pathname === "/dashboard" || location.pathname === "/"} 
          onClick={() => navigate("/dashboard")}
          activeColor="from-blue-400 via-blue-500 to-blue-600"
          inactiveColor="text-blue-400"
        />
        <NavButton 
          icon={<MessageSquareText />} 
          firstLine="Ask"
          secondLine="Strykr" 
          isActive={location.pathname === "/chat"} 
          onClick={() => navigate("/chat")}
          activeColor="from-indigo-400 via-purple-500 to-pink-500"
          inactiveColor="text-violet-400"
        />
        <NavButton 
          icon={<Calendar />} 
          firstLine="Economic"
          secondLine="Calendar" 
          isActive={location.pathname === "/calendar"} 
          onClick={() => navigate("/calendar")}
          activeColor="from-amber-400 via-orange-500 to-red-600"
          inactiveColor="text-amber-400"
        />
        <NavButton 
          icon={<BellRing />} 
          firstLine="Alpha"
          secondLine="Alerts" 
          isActive={location.pathname === "/alerts"} 
          onClick={() => navigate("/alerts")}
          activeColor="from-red-400 via-rose-500 to-pink-600"
          inactiveColor="text-rose-400"
        />
        <NavButton 
          icon={<Wand2 />} 
          firstLine="Market"
          secondLine="Picks" 
          isActive={location.pathname === "/market-screener"} 
          onClick={() => navigate("/market-screener")}
          activeColor="from-green-400 via-emerald-500 to-teal-600"
          inactiveColor="text-emerald-400"
        />
        <NavButton 
          icon={<Coins />} 
          firstLine="Wallet"
          secondLine="Analyzer" 
          isActive={location.pathname === "/wallet-analyzer"} 
          onClick={() => navigate("/wallet-analyzer")}
          activeColor="from-blue-500 via-indigo-600 to-violet-700"
          inactiveColor="text-blue-400"
        />
        <NavButton 
          icon={<ShieldCheck />} 
          firstLine="Account"
          secondLine="" 
          isActive={location.pathname === "/profile"} 
          onClick={() => navigate("/profile")}
          activeColor="from-sky-500 via-blue-600 to-indigo-700"
          inactiveColor="text-sky-400"
        />
      </div>
    </nav>
  );
};

interface NavButtonProps {
  icon: React.ReactNode;
  firstLine: string;
  secondLine: string;
  label?: string; // Keeping for backward compatibility
  isActive: boolean;
  onClick: () => void;
  activeColor?: string;
  inactiveColor?: string;
}

const NavButton = ({ icon, firstLine, secondLine, isActive, onClick, activeColor = "from-blue-600/90 to-indigo-600/90", inactiveColor = "text-slate-400" }: NavButtonProps) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex flex-col items-center justify-center gap-0.5 py-1 px-0.5 rounded-lg transition-all duration-300 w-[16%]",
        isActive 
          ? `text-white bg-gradient-to-br ${activeColor} shadow-md border border-white/10 backdrop-blur-sm` 
          : `${inactiveColor} hover:text-white hover:bg-slate-800/40`
      )}
    >
      <div className={cn(
        "transition-all duration-300 text-white", 
        isActive 
          ? "scale-110 filter drop-shadow-glow sidebar-icon-active" 
          : "opacity-90"
      )}>
        {React.cloneElement(icon as React.ReactElement, { 
          className: "w-3.5 h-3.5" 
        })}
      </div>
      <div className="flex flex-col items-center justify-center min-h-[18px]">
        <span className="text-[9px] font-medium leading-tight text-center">{firstLine}</span>
        {secondLine && <span className="text-[9px] font-medium leading-tight text-center">{secondLine}</span>}
      </div>
    </button>
  );
};
